import { Skeleton } from "@/components/ui/skeleton";

/**
 * Variantes de esqueleto de página inteira (padrão "tudo que carrega mostra
 * carregamento", decreto 03/set/2026) — cada uma imita a silhueta real do
 * tipo de tela que ela substitui, pra troca pro conteúdo real não "pular" o
 * layout. Toda variante é Server Component puro (sem `Date.now()`/`window`)
 * e usada só dentro de `loading.tsx` (fallback automático do Suspense de
 * rota do Next).
 *
 * Acessibilidade: cada variante é uma região `role="status"` com um único
 * `<span className="sr-only">Carregando…</span>` — um anúncio por tela, não
 * um por bloco (os blocos individuais, `Skeleton`, já são `aria-hidden`).
 *
 * Regra da Key (CLAUDE.md/DESIGN.md): esta é uma troca de CARREGAMENTO, não
 * de comportamento — nenhuma variante lê sessão, cookie ou dado real. As
 * telas públicas (login, observador/entrar) usam um esqueleto propositalmente
 * discreto: nunca um campo/rótulo que pareça formulário funcional pronto pra
 * receber clique antes da hora.
 */

function SrStatus() {
  return <span className="sr-only">Carregando…</span>;
}

/** Linha "← Voltar" — presente na maioria das telas internas (ContextualBackButton). */
function BackSkeleton() {
  return <Skeleton className="h-4 w-16" />;
}

function HeaderSkeleton({ titleWidth = "w-56", withSubtitle = true }: { titleWidth?: string; withSubtitle?: boolean }) {
  return (
    <>
      <Skeleton className={`h-7 ${titleWidth} mt-2 mb-1`} />
      {withSubtitle && <Skeleton className="h-4 w-full max-w-md mt-1 mb-6" />}
    </>
  );
}

/** Uma "linha" de lista com ícone/avatar + 2 textos + pill final (toggle) — AppRow/AppAccessRow/MemberGroupRow etc. */
function RowSkeleton({ withAvatar = true, trailing = "toggle" }: { withAvatar?: boolean; trailing?: "toggle" | "pill" | "none" }) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-800/50 border border-slate-700/50">
      {withAvatar && <Skeleton className="w-10 h-10 rounded-xl shrink-0" />}
      <div className="flex-1 min-w-0 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      {trailing === "toggle" && <Skeleton className="w-12 h-7 rounded-full shrink-0" />}
      {trailing === "pill" && <Skeleton className="h-7 w-20 rounded-lg shrink-0" />}
    </div>
  );
}

/** Rótulo + campo — usado nas barras de filtro e formulários. */
function FieldSkeleton({ labelWidth = "w-24" }: { labelWidth?: string }) {
  return (
    <div className="space-y-1.5">
      <Skeleton className={`h-3 ${labelWidth}`} />
      <Skeleton className="h-9 w-full rounded-lg" />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Público (sem chrome do app — login, observador/entrar)                 */
/* ────────────────────────────────────────────────────────────────────── */

/**
 * `/login` — card central com providers + tabs + formulário. Discreta de
 * propósito: nenhum campo tem `name`/`onChange`, é só a SILHUETA do card
 * real (mesma proporção de src/app/login/page.tsx) — não pode ser confundida
 * com um formulário funcional pronto antes da hora.
 */
export function LoginPageSkeleton() {
  return (
    <div role="status" className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <SrStatus />
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <Skeleton className="h-12 w-12 rounded-full mb-3 bg-slate-800" />
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-4 w-56 mt-2" />
        </div>
        <div className="bg-slate-900 rounded-2xl p-6 shadow-xl space-y-4">
          <Skeleton className="h-10 w-full rounded-lg" />
          <div className="flex items-center gap-3 py-1">
            <Skeleton className="h-px flex-1" />
            <Skeleton className="h-3 w-6" />
            <Skeleton className="h-px flex-1" />
          </div>
          <Skeleton className="h-10 w-full rounded-lg" />
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-9 w-full rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-9 w-full rounded-lg" />
          </div>
          <Skeleton className="h-10 w-full rounded-lg bg-slate-700" />
        </div>
      </div>
    </div>
  );
}

/**
 * `/observador/entrar` — card pequeno central (magic link, sem formulário:
 * a página só reage ao token da URL e redireciona). Espelha a proporção do
 * card real de src/app/observador/entrar/page.tsx.
 */
export function PublicNoticeSkeleton() {
  return (
    <div role="status" className="flex min-h-screen items-center justify-center bg-[#0f172a] px-4">
      <SrStatus />
      <div className="rounded-2xl bg-white/5 px-8 py-6 w-full max-w-xs space-y-3">
        <Skeleton className="h-3 w-32 bg-white/10" />
        <Skeleton className="h-6 w-40 bg-white/10" />
        <Skeleton className="h-3 w-48 bg-white/10" />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Portal e hub de admin                                                  */
/* ────────────────────────────────────────────────────────────────────── */

/** `/` — portal: cabeçalho + grid de apps + atalhos de admin + rodapé (src/app/page.tsx). */
export function PortalSkeleton() {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between mb-10 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-3 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-8 w-16 rounded-lg shrink-0" />
        </div>

        <Skeleton className="h-3 w-24 mb-3" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-2 rounded-2xl border border-slate-800 p-5">
              <Skeleton className="w-12 h-12 rounded-xl" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>

        <div className="mt-10">
          <Skeleton className="h-3 w-16 mb-4" />
          <div className="flex flex-wrap gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-36 rounded-xl" />
            ))}
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col items-center gap-3">
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
    </div>
  );
}

/** `/admin` — hub: back + título + grid de módulos (ícone + 2 linhas) — src/app/admin/page.tsx. */
export function HubGridSkeleton({ cards = 9 }: { cards?: number }) {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className="max-w-3xl mx-auto">
        <BackSkeleton />
        <HeaderSkeleton titleWidth="w-80" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: cards }).map((_, i) => (
            <div key={i} className="flex items-start gap-4 p-5 rounded-2xl bg-slate-800/50 border border-slate-700/50">
              <Skeleton className="w-8 h-8 rounded shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Listas e tabelas (admin/apps, admin/members, admin/grupos, auditoria…) */
/* ────────────────────────────────────────────────────────────────────── */

/** admin/apps, admin/members, admin/grupos — back+título + ações + filtro opcional + lista de linhas. */
export function ListPageSkeleton({
  rows = 6,
  withActionsRow = false,
  withFilterBar = false,
  filterFields = 4,
  maxWidth = "max-w-3xl",
}: {
  rows?: number;
  withActionsRow?: boolean;
  withFilterBar?: boolean;
  filterFields?: number;
  maxWidth?: string;
}) {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className={`${maxWidth} mx-auto`}>
        <BackSkeleton />
        <HeaderSkeleton titleWidth="w-52" />

        {withActionsRow && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <Skeleton className="h-14 flex-1 min-w-[200px] rounded-2xl" />
            <Skeleton className="h-9 w-44 rounded-xl" />
          </div>
        )}

        {withFilterBar && (
          <div className="mb-4 rounded-2xl bg-slate-800/30 border border-slate-700/50 p-4">
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${filterFields > 3 ? "lg:grid-cols-4" : ""} gap-3`}>
              {Array.from({ length: filterFields }).map((_, i) => (
                <FieldSkeleton key={i} />
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {Array.from({ length: rows }).map((_, i) => (
            <RowSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** admin/auditoria, admin/acessos — back+título + stats opcionais + tabela(s). */
export function TableSectionsSkeleton({
  withStatCards = false,
  sections = 1,
  rows = 6,
  cols = 4,
  maxWidth = "max-w-5xl",
}: {
  withStatCards?: boolean;
  sections?: number;
  rows?: number;
  cols?: number;
  maxWidth?: string;
}) {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className={`${maxWidth} mx-auto`}>
        <BackSkeleton />
        <HeaderSkeleton titleWidth="w-40" />

        {withStatCards && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 -mt-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-slate-800 p-3 space-y-2">
                <Skeleton className="h-6 w-10" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        )}

        {Array.from({ length: sections }).map((_, s) => (
          <div key={s} className="mt-8 first:mt-0">
            <Skeleton className="h-3 w-40 mb-3" />
            <div className="overflow-hidden rounded-xl border border-slate-800">
              <div className="flex gap-4 bg-slate-800/30 px-3 py-2.5">
                {Array.from({ length: cols }).map((_, c) => (
                  <Skeleton key={c} className="h-3 flex-1" />
                ))}
              </div>
              <div className="divide-y divide-slate-800/70">
                {Array.from({ length: rows }).map((_, r) => (
                  <div key={r} className="flex gap-4 px-3 py-3">
                    {Array.from({ length: cols }).map((_, c) => (
                      <Skeleton key={c} className="h-3.5 flex-1" />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Telas de detalhe (members/[id], grupos/[id], apps/[id])                */
/* ────────────────────────────────────────────────────────────────────── */

/**
 * admin/members/[id], admin/grupos/[id] — back + cabeçalho de perfil (avatar
 * + nome + badges) + card de resumo + N seções com lista de linhas.
 */
export function DetailPageSkeleton({
  sections = 2,
  rowsPerSection = 3,
  maxWidth = "max-w-3xl",
}: {
  sections?: number;
  rowsPerSection?: number;
  maxWidth?: string;
}) {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className={`${maxWidth} mx-auto`}>
        <BackSkeleton />
        <div className="flex items-center gap-4 mt-4 mb-6">
          <Skeleton className="w-14 h-14 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-4 mb-8">
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-4 w-28" />
              </div>
            ))}
          </div>
        </div>

        {Array.from({ length: sections }).map((_, s) => (
          <div key={s} className="mb-8">
            <Skeleton className="h-3 w-32 mb-3" />
            <div className="space-y-3">
              {Array.from({ length: rowsPerSection }).map((_, r) => (
                <RowSkeleton key={r} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** admin/apps/[id] — back+título(app) + form "novo recurso" + lista compacta de recursos. */
export function AppDetailSkeleton() {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className="max-w-3xl mx-auto">
        <BackSkeleton />
        <div className="flex items-center gap-3 mt-2 mb-1">
          <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
          <Skeleton className="h-7 w-44" />
        </div>
        <Skeleton className="h-4 w-64 mt-1 mb-6" />

        <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-4 mb-6">
          <Skeleton className="h-4 w-32 mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldSkeleton labelWidth="w-16" />
            <FieldSkeleton labelWidth="w-28" />
            <Skeleton className="h-10 sm:col-span-2 rounded-xl" />
          </div>
        </div>

        <div className="space-y-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <RowSkeleton key={i} withAvatar={false} trailing="pill" />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Assistente, dashboard, seções e conteúdo público                       */
/* ────────────────────────────────────────────────────────────────────── */

/** admin/grupos/novo — assistente em passos (src/app/admin/grupos/novo/wizard.tsx). */
export function WizardSkeleton({ steps = 5 }: { steps?: number }) {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className="max-w-4xl mx-auto">
        <BackSkeleton />
        <HeaderSkeleton titleWidth="w-64" />

        <div className="flex items-center gap-2 mb-6">
          {Array.from({ length: steps }).map((_, i) => (
            <Skeleton key={i} className="h-1.5 flex-1 rounded-full" />
          ))}
        </div>

        <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-5 space-y-4">
          <FieldSkeleton labelWidth="w-24" />
          <FieldSkeleton labelWidth="w-32" />
          <div className="grid grid-cols-2 gap-3">
            <FieldSkeleton labelWidth="w-20" />
            <FieldSkeleton labelWidth="w-20" />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="h-9 w-24 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/** `/saude` — stats da suite + lista de status por app (src/app/saude/page.tsx). */
export function DashboardStatsSkeleton() {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className="max-w-5xl mx-auto">
        <BackSkeleton />
        <HeaderSkeleton titleWidth="w-56" />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-4 space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-7 w-14" />
            </div>
          ))}
        </div>

        <Skeleton className="h-3 w-32 mb-3" />
        <div className="grid gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-700/50 bg-slate-800/30 p-4">
              <div className="flex-1 space-y-2 min-w-0">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-56" />
              </div>
              <Skeleton className="h-6 w-14 rounded-full shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** admin/cargos — back+título + N seções, cada uma com grid de cartões pequenos. */
export function SectionsGridSkeleton({ sections = 4 }: { sections?: number }) {
  return (
    <div role="status" className="min-h-screen p-6 sm:p-10">
      <SrStatus />
      <div className="max-w-3xl mx-auto">
        <BackSkeleton />
        <HeaderSkeleton titleWidth="w-72" />

        {Array.from({ length: sections }).map((_, s) => (
          <div key={s} className="mb-8">
            <Skeleton className="h-4 w-40 mb-3" />
            <div className="grid gap-2 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-xl" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** `/tutorial` — busca + índice de capítulos + conteúdo (público, sem chrome do app). */
export function TutorialSkeleton() {
  return (
    <div role="status" className="mx-auto max-w-5xl px-6 py-6 sm:px-10 sm:py-10">
      <SrStatus />
      <Skeleton className="h-8 w-64 mb-2" />
      <Skeleton className="h-4 w-96 max-w-full mb-6" />
      <Skeleton className="h-11 w-full rounded-xl mb-8" />
      <div className="grid gap-6 md:grid-cols-[240px_1fr]">
        <div className="hidden md:block space-y-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-4" style={{ width: `${85 - (i % 3) * 15}%` }} />
          ))}
        </div>
        <div className="space-y-3">
          <Skeleton className="h-6 w-1/2" />
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-4" style={{ width: `${92 - (i % 4) * 12}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}
